import { Link, Navigate } from "react-router-dom";
import { Button, Tooltip } from "@mui/material";
import {
  CustomerTilesNoTitlesMessageStyles,
  CustomerTilesPanelStyles,
} from "../CustomerTiles/CustomerTilesPanelStyles";
import { SingleCustomerTileStyles } from "../CustomerTiles/SingleCustomerTileStyles";
import { useIsDarkMode } from "store/atoms";
import { useState } from "react";
import { SearchCustomers } from "../CustomerTiles/SearchCustomers";
import { useIsAdmin } from "utils/customHooks/genericHooks";
import { getTimeZoneOffsetMsFromLocaleString } from "utils/timeZoneHooks";
import { useShouldRedirectIntoFirstGroup } from "../GroupTile/GroupTilesPanel";
import useFlagsWithDefaults from "utils/customHooks/useFlagsWithDefaults";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
export type WorkScheduleLink = {
  name: string;
  id: string;
  link: string;
  linkParams: string[];
  rollupShortName?: string;
  timeZone?: string;
};
export type WorkScheduleTileProps = {
  workScheduleList: WorkScheduleLink[];
};

const ALL_WORKSCHEDULES = "ALL_WORKSCHEDULES";
const WorkScheduleTilesPanel = ({
  workScheduleList,
}: WorkScheduleTileProps) => {
  const [searchTermOriginal, setSearchTerm] = useState("");
  const searchTerm = searchTermOriginal.toLowerCase();
  const { flightShowAllWorkSchedulesPage } = useFlagsWithDefaults();
  const ckey = useCustomerKey();
  const handleSearchChange = e => {
    setSearchTerm(e.target.value);
  };
  const handleClearSearch = () => {
    setSearchTerm("");
  };

  if (workScheduleList.length === 0) {
    return (
      <CustomerTilesNoTitlesMessageStyles></CustomerTilesNoTitlesMessageStyles>
    );
  } else if (workScheduleList.length === 1) {
    const redirectLink =
      workScheduleList[0].link +
      "/dashboard?" +
      workScheduleList[0].linkParams.join("&");
    return <Navigate replace={true} to={`${redirectLink}`} />;
  }
  const shouldShowSearch = workScheduleList.length > 8;

  return (
    <div data-component-name={"Work Schedule Tiles Page"}>
      {shouldShowSearch ? (
        <SearchCustomers
          {...{
            label: "Search work schedules...",
            searchTerm,
            handleSearchChange,
            handleClearSearch,
          }}
        />
      ) : null}
      <CustomerTilesPanelStyles
        $shouldShowSearch={shouldShowSearch}
        data-component-name={"Work Schedule Tiles Panel"}
        style={shouldShowSearch ? { marginTop: 0 } : {}}
      >
        {flightShowAllWorkSchedulesPage ? (
          <WorkScheduleTileContent
            {...{
              link: `/${ckey}`,
              linkParams: ["workSchedule=all"],
              rollupShortName: "ALL",
              ckey,
              timeZone: workScheduleList
                .reduce((acc, cur) => {
                  // accumulate all unique timezones
                  if (!cur.timeZone || acc.includes(cur.timeZone)) {
                    return acc;
                  }
                  return acc.concat(cur.timeZone);
                }, [] as string[])
                .join(", "),
              id: ALL_WORKSCHEDULES,
            }}
          />
        ) : null}
        {workScheduleList
          .filter(
            ({ name: ckey, rollupShortName }) =>
              Boolean(ckey || rollupShortName) &&
              (!searchTerm ||
                ckey.includes(searchTerm) ||
                rollupShortName?.toLowerCase().includes(searchTerm))
          )
          .map(
            ({
              name: ckey,
              link,
              linkParams,
              rollupShortName,
              timeZone,
              id,
            }) => {
              return (
                <WorkScheduleTileContent
                  key={ckey}
                  {...{
                    link,
                    linkParams,
                    rollupShortName,
                    ckey,
                    timeZone,
                    id,
                  }}
                />
              );
            }
          )}
      </CustomerTilesPanelStyles>
    </div>
  );
};

export default WorkScheduleTilesPanel;

function WorkScheduleTileContent({
  link,
  linkParams,
  rollupShortName,
  ckey,
  timeZone,
  id,
}: {
  link: string;
  linkParams: string[];
  rollupShortName?: string;
  ckey: string;
  timeZone?: string;
  id: string;
}) {
  const [isDarkMode] = useIsDarkMode();

  const landingPage = "dashboard";
  const fullLink = link + "/" + landingPage;
  const isAdminUser = useIsAdmin();
  const { tzUtcOffset } = getTimeZoneOffsetMsFromLocaleString(timeZone);
  const timezoneReadable =
    id === ALL_WORKSCHEDULES ? timeZone : `${timeZone} (UTC ${tzUtcOffset})`;
  const { shouldRedirect, redirectToUrl } = useShouldRedirectIntoFirstGroup(id);
  return (
    <Link
      to={
        shouldRedirect && id !== ALL_WORKSCHEDULES
          ? redirectToUrl
          : { pathname: `${fullLink}`, search: linkParams.join("&") }
      }
      style={{ textDecoration: "none" }}
    >
      <Tooltip
        title={isAdminUser && timeZone ? timezoneReadable : ""}
        disableInteractive={true}
      >
        <SingleCustomerTileStyles
          className="customerTileStyles"
          key={ckey}
          $isDarkMode={isDarkMode}
          $isLastSelected={false}
        >
          <Button className="customerTileButton">
            <SingleCustomerTile
              name={rollupShortName || ckey}
              ckey={fullLink}
            />
          </Button>
        </SingleCustomerTileStyles>
      </Tooltip>
    </Link>
  );
}

function SingleCustomerTile({ name, ckey }: { name: string; ckey: string }) {
  return (
    <div
      className="singleCustomerTile"
      data-component-name={"Work Schedule Tile"}
    >
      <div className="top-group-title">{name}</div>
      <div className="top-group-subtitle">flight.raven.ai{ckey}</div>
    </div>
  );
}
